import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import discoveryService from "./discoveryService";
import { discoveryKeys } from "./communityQueries";

/**
 * Hook to fetch discovery homepage
 * @returns {Object} - React Query result
 */
export const useDiscoveryHomepage = () => {
  return useQuery({
    queryKey: discoveryKeys.homepage(),
    queryFn: async () => {
      const response = await discoveryService.getDiscoveryHomepage();
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch popular communities
 * @param {string} sortBy - Sort by 'members' or 'activity'
 * @param {number} limit - Number of communities
 * @returns {Object} - React Query result
 */
export const usePopularCommunities = (sortBy = "members", limit = 10) => {
  return useQuery({
    queryKey: discoveryKeys.popular(sortBy, limit),
    queryFn: async () => {
      const response = await discoveryService.getPopularCommunities(
        sortBy,
        limit
      );
      return response.data;
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch recommended communities (personalized)
 * @param {number} limit - Number of communities
 * @returns {Object} - React Query result
 */
export const useRecommendedCommunities = (limit = 10) => {
  return useQuery({
    queryKey: discoveryKeys.recommended(limit),
    queryFn: async () => {
      const response = await discoveryService.getRecommendedCommunities(limit);
      return response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (personalized data changes slowly)
    cacheTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to search communities with debouncing
 * @param {string} query - Search query
 * @param {number} limit - Number of results
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 500)
 * @returns {Object} - React Query result with debounced query
 */
export const useSearchCommunities = (query, limit = 20, debounceMs = 500) => {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  // Debounce the query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [query, debounceMs]);

  return useQuery({
    queryKey: discoveryKeys.search(debouncedQuery, limit),
    queryFn: async () => {
      const response = await discoveryService.searchCommunities(
        debouncedQuery,
        limit
      );
      return response.data;
    },
    enabled: debouncedQuery.trim().length >= 2, // Only search if query is 2+ chars
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch communities by field
 * @param {string} field - Research field name
 * @param {number} limit - Items per page
 * @param {number} page - Page number
 * @returns {Object} - React Query result
 */
export const useCommunitiesByField = (field, limit = 20, page = 1) => {
  return useQuery({
    queryKey: discoveryKeys.byField(field, limit, page),
    queryFn: async () => {
      const response = await discoveryService.getCommunitiesByField(
        field,
        limit,
        page
      );
      return response.data;
    },
    enabled: !!field, // Only fetch if field is provided
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch communities by topic
 * @param {string} topic - Topic slug
 * @param {number} limit - Items per page
 * @param {number} page - Page number
 * @returns {Object} - React Query result
 */
export const useCommunitiesByTopic = (topic, limit = 20, page = 1) => {
  return useQuery({
    queryKey: discoveryKeys.byTopic(topic, limit, page),
    queryFn: async () => {
      const response = await discoveryService.getCommunitiesByTopic(
        topic,
        limit,
        page
      );
      return response.data;
    },
    enabled: !!topic, // Only fetch if topic is provided
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch all topics
 * @param {number} limit - Number of topics
 * @returns {Object} - React Query result
 */
export const useTopics = (limit = 50) => {
  return useQuery({
    queryKey: discoveryKeys.topics(limit),
    queryFn: async () => {
      const response = await discoveryService.getAllTopics(limit);
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour (topics rarely change)
    cacheTime: 2 * 60 * 60 * 1000, // 2 hours
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch all fields
 * @returns {Object} - React Query result
 */
export const useFields = () => {
  return useQuery({
    queryKey: discoveryKeys.fields(),
    queryFn: async () => {
      const response = await discoveryService.getAllFields();
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour (fields rarely change)
    cacheTime: 2 * 60 * 60 * 1000, // 2 hours
    refetchOnWindowFocus: false,
  });
};
